/* ==========================================================================
   Laurel Box — Cart drawer custom add-ons
   - Free shipping progress bar
   - Gift message + Remembrance capture (saved as cart attributes + Klaviyo)
   - Recommended products (Search & Discovery / Recommendations API)
   Depends on the Impulse theme's cart events: `cart:updated`,
   `ajaxProduct:added`, `cart:open`. Safe if any block is disabled.
   ========================================================================== */
(function () {
  'use strict';

  var SELECTORS = {
    bar: '[data-free-shipping-bar]',
    attr: '[data-cart-attribute]',
    giftBody: '#CartGiftBody',
    giftCount: '[data-gift-count]',
    recsWrap: '[data-cart-recommendations]',
    recsResults: '[data-cart-recs-results]',
    customerEmail: '#CartDrawerForm'
  };

  /* ---------- helpers ---------- */
  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.settings && theme.settings.moneyFormat) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      return r.json();
    });
  }

  /* ---------- 1. Free shipping bar ---------- */
  function updateFreeShipping(cart) {
    var bar = document.querySelector(SELECTORS.bar);
    if (!bar) return;

    var threshold = parseInt(bar.dataset.threshold, 10) || 0;
    var msgEl = bar.querySelector('[data-fsb-message]');
    var fillEl = bar.querySelector('[data-fsb-fill]');
    if (threshold <= 0) { bar.hidden = true; return; }
    bar.hidden = false;

    var total = cart.total_price || 0;
    var remaining = threshold - total;
    var pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));
    if (fillEl) fillEl.style.width = pct + '%';

    if (remaining <= 0) {
      bar.classList.add('is-complete');
      if (msgEl) msgEl.innerHTML = bar.dataset.success || '';
    } else {
      bar.classList.remove('is-complete');
      if (msgEl) msgEl.innerHTML = (bar.dataset.message || '').replace('[amount]', formatMoney(remaining));
    }
  }

  /* ---------- 2. Cart attribute capture (gift + remembrance) ---------- */
  var saveTimer = null;
  var pending = {};

  function flushSave() {
    var keys = Object.keys(pending);
    if (!keys.length) return;
    var attrs = {};
    keys.forEach(function (k) { attrs[k] = pending[k]; });
    pending = {};
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attrs })
    }).catch(function () {});
  }

  function queueSave(key, value) {
    pending[key] = value;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(flushSave, 600);
  }

  function customerEmail() {
    var form = document.querySelector(SELECTORS.customerEmail);
    return (form && form.dataset.customerEmail) ? form.dataset.customerEmail : null;
  }

  // Push field values onto the current Klaviyo profile (if one is known on
  // this browser). No-op when no profile/email is available — the value still
  // reaches Klaviyo via the order (cart attributes) either way.
  function klaviyoIdentify(props) {
    try {
      var email = customerEmail();
      var payload = {};
      if (email) payload.$email = email;
      Object.keys(props).forEach(function (k) { payload[k] = props[k]; });
      if (email || (window.klaviyo && klaviyo.isIdentified) || window._learnq) {
        if (window.klaviyo && typeof klaviyo.identify === 'function') {
          klaviyo.identify(payload);
        } else {
          window._learnq = window._learnq || [];
          window._learnq.push(['identify', payload]);
        }
      }
    } catch (e) { /* Klaviyo not present — ignore */ }
  }

  function bindAttributeFields() {
    document.querySelectorAll(SELECTORS.attr).forEach(function (el) {
      if (el.dataset.cartBound) return;
      el.dataset.cartBound = '1';

      el.addEventListener('input', function () {
        queueSave(el.dataset.cartAttribute, el.value);
      });
      el.addEventListener('change', function () {
        queueSave(el.dataset.cartAttribute, el.value);
        flushSave();
        if (el.dataset.klaviyoKey && el.value) {
          var props = {};
          props[el.dataset.klaviyoKey] = el.value;
          klaviyoIdentify(props);
        }
      });
    });

    // Gift message character counter
    var body = document.querySelector(SELECTORS.giftBody);
    var count = document.querySelector(SELECTORS.giftCount);
    if (body && count && !body.dataset.counterBound) {
      body.dataset.counterBound = '1';
      body.addEventListener('input', function () { count.textContent = body.value.length; });
    }
  }

  /* ---------- 3. Recommended products ---------- */
  function updateRecommendations(cart) {
    var wrap = document.querySelector(SELECTORS.recsWrap);
    if (!wrap) return;

    if (!cart.items || !cart.items.length) { wrap.hidden = true; return; }

    var limit = parseInt(wrap.dataset.limit, 10) || 4;
    var productId = cart.items[0].product_id;
    var signature = cart.item_count + ':' + productId;
    if (wrap.dataset.signature === signature) return; // already showing the right set

    var results = wrap.querySelector(SELECTORS.recsResults);
    var url = '/recommendations/products?section_id=cart-recommendations&intent=related&limit=' +
      (limit + 4) + '&product_id=' + productId;

    fetch(url).then(function (r) { return r.text(); }).then(function (text) {
      var div = document.createElement('div');
      div.innerHTML = text;
      var grid = div.querySelector('.cart-recs__grid');
      if (!grid) { wrap.hidden = true; return; }

      // Drop anything already in the cart, then cap to the configured limit
      var inCart = cart.items.map(function (i) { return String(i.product_id); });
      var cards = Array.prototype.slice.call(grid.querySelectorAll('.cart-recs__card'));
      var shown = 0;
      cards.forEach(function (card) {
        if (inCart.indexOf(card.dataset.productId) !== -1 || shown >= limit) {
          card.parentNode.removeChild(card);
        } else {
          shown++;
        }
      });

      if (shown === 0) { wrap.hidden = true; return; }
      results.innerHTML = grid.outerHTML;
      wrap.hidden = false;
      wrap.dataset.signature = signature;
    }).catch(function () { wrap.hidden = true; });
  }

  function bindRecommendationAdd() {
    if (document.body.dataset.recsAddBound) return;
    document.body.dataset.recsAddBound = '1';
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-cart-recs-add]');
      if (!btn) return;
      e.preventDefault();
      var vid = btn.dataset.variantId;
      if (!vid || btn.disabled) return;

      btn.disabled = true;
      btn.classList.add('is-loading');
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: vid, quantity: 1 }] })
      }).then(function (r) { return r.json(); }).then(function () {
        document.dispatchEvent(new CustomEvent('cart:build')); // rebuild line items
        document.dispatchEvent(new CustomEvent('cart:open'));  // keep drawer open
        return getCart();
      }).then(function (cart) {
        if (cart) refresh(cart);
      }).catch(function () {}).then(function () {
        btn.disabled = false;
        btn.classList.remove('is-loading');
      });
    });
  }

  /* ---------- orchestration ---------- */
  function refresh(cart) {
    updateFreeShipping(cart);
    updateRecommendations(cart);
  }

  function refreshFromServer() {
    getCart().then(refresh).catch(function () {});
  }

  function init() {
    bindAttributeFields();
    bindRecommendationAdd();
    refreshFromServer();

    document.addEventListener('cart:updated', function (e) {
      if (e.detail && e.detail.cart) { refresh(e.detail.cart); } else { refreshFromServer(); }
    });
    document.addEventListener('ajaxProduct:added', refreshFromServer);
    document.addEventListener('cart:open', refreshFromServer);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
